import { Link } from "react-router-dom";
import { ArrowRight, Coins, TrendingUp, Landmark, Users, Handshake, LineChart } from "lucide-react";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/315f67a0-df9f-4f8c-bd82-001e5f5d1a6a/images/ab036d2fcb506bbd2fc2ee612c1c8372c6fef171444ad6b0ca6cb66b3ef68815.jpeg";

const SERVICES = [
  { icon: Coins, title: "DEBT FUNDING", desc: "Structured debt solutions to fuel growth and strengthen cash flow.", key: "debt-funding" },
  { icon: TrendingUp, title: "EQUITY FUNDING", desc: "Growth capital to scale businesses and unlock new opportunities.", key: "equity-funding" },
  { icon: Landmark, title: "PROJECT FINANCE", desc: "End-to-end financing for infrastructure and industrial projects.", key: "project-finance" },
  { icon: Users, title: "STRATEGIC ADVISORY", desc: "Expert guidance to optimize strategy and drive sustainable growth.", key: "strategic-advisory" },
  { icon: Handshake, title: "MERGERS & ACQUISITIONS", desc: "Advisory support for mergers, acquisitions and strategic partnerships.", key: "mergers-acquisitions" },
];

export default function CapitalAdvisory() {
  return (
    <div data-testid="capital-advisory-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-slate-800" data-testid="capital-hero">
        <img src={HERO_IMG} alt="Global capital advisory" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-950 via-navy-950/80 to-navy-950/25" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-24 md:pt-28 pb-20">
          <div className="max-w-2xl">
            <p className="font-mono text-[11px] md:text-xs tracking-[0.3em] text-techblue-light mb-5 animate-fade-up" data-testid="capital-kicker">GLOBAL CAPITAL ADVISORY</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] animate-fade-up stagger-1" data-testid="capital-headline">
              <span className="text-slate-100">FUNDING BEYOND</span>
              <span className="block mt-1.5 text-techblue-light">EXPECTATIONS</span>
            </h1>
            <div className="w-16 h-px bg-techblue-light my-7 animate-fade-up stagger-2" />
            <p className="text-base md:text-lg text-slate-300 leading-relaxed max-w-lg animate-fade-up stagger-2">
              10XGLOBAL is a global capital advisory firm delivering innovative funding solutions, strategic advisory, and long-term value for businesses worldwide.
            </p>
            <div className="mt-9 animate-fade-up stagger-3">
              <a href="#our-services" data-testid="explore-services-btn" className="btn-outline !px-7 !py-3.5 text-sm">EXPLORE SERVICES <ArrowRight size={16} /></a>
            </div>
          </div>
        </div>
      </section>

      {/* PROMISE STRIP */}
      <section className="max-w-4xl mx-auto px-4 md:px-8 pt-16 pb-4 text-center" data-testid="capital-promise">
        <p className="font-mono text-[11px] md:text-xs tracking-[0.3em] text-techblue-light" data-testid="capital-promise-title">GLOBAL REACH. STRATEGIC CAPITAL. LASTING IMPACT.</p>
        <p className="mt-4 text-sm md:text-base text-slate-400 leading-relaxed max-w-2xl mx-auto">
          We connect businesses with the right capital, partners, and opportunities to accelerate growth and create lasting value across global markets.
        </p>
      </section>

      {/* OUR SERVICES */}
      <section id="our-services" className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-20" data-testid="advisory-services">
        <p className="font-mono text-[11px] tracking-[0.3em] text-techblue-light mb-10" data-testid="our-services-label">— OUR SERVICES</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {SERVICES.map((s) => (
            <div key={s.key} className="card-surface p-6 text-center flex flex-col items-center hover:border-techblue/50 transition-colors" data-testid={`service-${s.key}`}>
              <div className="w-14 h-14 rounded-full border border-techblue/40 bg-navy-900 flex items-center justify-center mb-5">
                <s.icon size={22} className="text-techblue-light" />
              </div>
              <p className="font-display font-bold tracking-wide text-slate-100 text-sm">{s.title}</p>
              <p className="text-xs text-slate-400 mt-3 leading-relaxed flex-1">{s.desc}</p>
              <Link to="/contact" data-testid={`learn-more-${s.key}`} className="mt-5 text-[11px] font-mono uppercase tracking-widest text-techblue-light hover:text-gold transition-colors inline-flex items-center gap-1.5">
                LEARN MORE <ArrowRight size={12} />
              </Link>
            </div>
          ))}
        </div>

        <div className="card-surface p-6 mt-10 flex flex-wrap items-center gap-6" data-testid="capital-process">
          <p className="text-[10px] font-mono uppercase tracking-widest text-gold w-full">How a mandate moves</p>
          {["MANDATE SCOPED", "INVESTOR MATCHING", "TERM DISCUSSIONS", "DILIGENCE", "CLOSE"].map((step, i, arr) => (
            <div key={step} className="flex items-center gap-6">
              <div className="flex items-center gap-3">
                <span className="w-7 h-7 rounded-full border border-gold/60 text-gold font-mono text-xs flex items-center justify-center">{i + 1}</span>
                <span className="text-xs font-mono uppercase tracking-widest text-slate-300">{step}</span>
              </div>
              {i < arr.length - 1 && <ArrowRight size={14} className="text-slate-600" />}
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-slate-800 bg-navy-900/40" data-testid="capital-cta">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-24 text-center">
          <LineChart size={28} className="text-gold mx-auto mb-6" />
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-100">YOUR CAPITAL REQUIREMENT<br />DESERVES THE RIGHT CAPITAL PARTNER.</h2>
          <p className="text-slate-400 mt-4 max-w-xl mx-auto">Post your funding requirement and the intelligence engine will match it against verified investors, lenders and strategic capital partners.</p>
          <div className="flex flex-wrap justify-center gap-4 mt-9">
            <Link to="/rfq" data-testid="post-capital-requirement-btn" className="btn-gold">POST CAPITAL REQUIREMENT <ArrowRight size={16} /></Link>
            <Link to="/opportunities?type=FUNDING%20REQUIREMENT" data-testid="view-capital-opportunities-btn" className="btn-outline">VIEW CAPITAL OPPORTUNITIES</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
